<template>
  <div 
    ref="containerRef"
    class="pull-to-refresh-container"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
  >
    <!-- Pull indicator - teleportován do body aby neovlivňoval layout -->
    <Teleport to="body">
      <div 
        v-if="showIndicator"
        class="pull-indicator"
        :style="indicatorStyle"
      >
        <div class="pull-indicator-content">
          <v-progress-circular
            v-if="isRefreshing"
            indeterminate
            color="primary"
            size="28"
            width="3"
          />
          <v-icon
            v-else
            :style="iconStyle"
            :color="pullProgress >= 1 ? 'primary' : 'grey'"
            size="28"
          >
            mdi-arrow-down
          </v-icon>
          <span class="pull-text" :class="{ 'text-primary': pullProgress >= 1 }">
            {{ statusText }}
          </span>
        </div>
      </div>
    </Teleport>
    
    <!-- Content - bez transform wrapperu -->
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';

const { t } = useI18n({ useScope: 'global' });
const display = useDisplay();

const emit = defineEmits<{
  (e: 'refresh'): void;
}>();

const props = withDefaults(defineProps<{
  disabled?: boolean;
  threshold?: number;
  maxPull?: number;
}>(), {
  disabled: false,
  threshold: 70,
  maxPull: 120
});

const containerRef = ref<HTMLElement | null>(null);
const startY = ref(0);
const currentY = ref(0);
const pullDistance = ref(0);
const isPulling = ref(false);
const isRefreshing = ref(false);

// Použijeme Vuetify breakpointy pro detekci mobilu
const isMobile = computed(() => display.smAndDown.value);

// Zobrazit indikátor
const showIndicator = computed(() => {
  return isMobile.value && (isPulling.value || isRefreshing.value || pullDistance.value > 0);
});

// Kontrola jestli jsme na vrcholu stránky
const isAtTop = () => {
  return window.scrollY <= 0;
};

// Progress 0-1 pro animace
const pullProgress = computed(() => {
  return Math.min(pullDistance.value / props.threshold, 1);
});

// Elastic easing pro gumový efekt
const elasticPull = computed(() => {
  const distance = pullDistance.value;
  if (distance <= props.threshold) {
    return distance;
  }
  // Gumový efekt po překročení thresholdu
  const overPull = distance - props.threshold;
  const dampedOverPull = overPull * 0.4; // Čím víc táhneš, tím větší odpor
  return Math.min(props.threshold + dampedOverPull, props.maxPull);
});

// Styl pro indikátor
const indicatorStyle = computed(() => {
  const translateY = elasticPull.value - 60; // Začíná schovaný nahoře
  return {
    transform: `translateY(${Math.max(translateY, -60)}px)`,
    opacity: Math.min(pullProgress.value * 1.5, 1),
    transition: isPulling.value ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s'
  };
});

// Styl pro ikonu (rotace)
const iconStyle = computed(() => {
  const rotation = pullProgress.value * 180;
  return {
    transform: `rotate(${rotation}deg)`,
    transition: isPulling.value ? 'transform 0.1s' : 'transform 0.3s'
  };
});

// Status text
const statusText = computed(() => {
  if (isRefreshing.value) {
    return t('pullToRefresh.refreshing', 'Načítám...');
  }
  if (pullProgress.value >= 1) {
    return t('pullToRefresh.release', 'Pusť pro obnovení');
  }
  return t('pullToRefresh.pull', 'Táhni dolů pro obnovení');
});

// Threshold (px) – finger must move down at least this much to activate pull
const PULL_ACTIVATE_THRESHOLD = 15;

// Non-passive touchmove handler (registered only while pull gesture is active)
const onPullTouchMove = (e: TouchEvent) => {
  currentY.value = e.touches[0].clientY;
  const diff = currentY.value - startY.value;

  if (diff > 0 && isAtTop()) {
    pullDistance.value = diff;
    e.preventDefault(); // prevent scroll during pull
  } else {
    pullDistance.value = 0;
  }
};

const attachPullListener = () => {
  containerRef.value?.addEventListener('touchmove', onPullTouchMove, { passive: false });
};

const detachPullListener = () => {
  containerRef.value?.removeEventListener('touchmove', onPullTouchMove);
};

// maybePulling = touchstart happened at top, waiting for confirmation
const maybePulling = ref(false);

const onTouchStart = (e: TouchEvent) => {
  if (!isMobile.value || props.disabled || isRefreshing.value || !isAtTop()) {
    return;
  }
  startY.value = e.touches[0].clientY;
  maybePulling.value = true;
  // Don't set isPulling here – wait for confirmed downward movement
  // so we don't interfere with normal tap/click events
};

// Passive touchmove to detect pull gesture start (registered via template)
// Once confirmed, switch to non-passive handler for preventDefault
const onTouchMovePassive = (e: TouchEvent) => {
  if (!maybePulling.value || isPulling.value) return;
  if (!isMobile.value || props.disabled || isRefreshing.value) return;

  const diff = e.touches[0].clientY - startY.value;
  if (diff > PULL_ACTIVATE_THRESHOLD && isAtTop()) {
    // Confirmed pull gesture – switch to non-passive handler
    isPulling.value = true;
    currentY.value = e.touches[0].clientY;
    pullDistance.value = diff;
    attachPullListener();
  }
};

const onTouchEnd = async () => {
  maybePulling.value = false;

  if (!isPulling.value) return;

  detachPullListener();
  isPulling.value = false;
  
  if (pullProgress.value >= 1 && !isRefreshing.value) {
    isRefreshing.value = true;
    emit('refresh');
    await new Promise(resolve => setTimeout(resolve, 800));
    isRefreshing.value = false;
  }
  
  pullDistance.value = 0;
};

// Register passive touchmove (gesture detection only — never calls preventDefault)
onMounted(() => {
  containerRef.value?.addEventListener('touchmove', onTouchMovePassive, { passive: true });
});

onBeforeUnmount(() => {
  containerRef.value?.removeEventListener('touchmove', onTouchMovePassive);
  detachPullListener();
});

// Globální refresh funkce kterou mohou použít stránky
const completeRefresh = () => {
  isRefreshing.value = false;
  pullDistance.value = 0;
};

defineExpose({
  completeRefresh,
  isRefreshing
});
</script>

<style scoped>
.pull-to-refresh-container {
  /* Žádné speciální styly - nechceme měnit layout */
}

.pull-indicator {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  pointer-events: none;
}

.pull-indicator-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: rgba(var(--v-theme-surface), 0.95);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
}

.pull-text {
  font-size: 11px;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.6);
  transition: color 0.2s;
  white-space: nowrap;
}

.pull-text.text-primary {
  color: rgb(var(--v-theme-primary));
}
</style>
